import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contentAPI } from '@/utils/api';
import { Shield } from 'lucide-react';
import { Loader } from '@/components/ui/loader';

export const Legal = () => {
  const { t, i18n } = useTranslation();
  const [legalContent, setLegalContent] = useState([]);
  const [llpDetails, setLlpDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [legalRes, llpRes] = await Promise.all([
        contentAPI.getLegalContent(),
        contentAPI.getLLPDetails()
      ]);
      setLegalContent(legalRes.data);
      setLlpDetails(llpRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching legal content:', error);
      setLoading(false);
    }
  };

  const getLangContent = (content) => {
    return content?.[i18n.language] || content?.en || '';
  };

  const getSectionTitle = (section) => {
    const titles = {
      confidentiality: 'Confidentiality',
      disclaimer: 'Disclaimer',
      jurisdiction: 'Jurisdiction',
      data_privacy: 'Data Privacy'
    };
    return titles[section] || section;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <Loader className="text-secondary" text={t('common.loading')} />
      </div>
    );
  }

  return (
    <div className="bg-primary min-h-screen">
      {/* Hero Section */}
      <section data-testid="legal-hero" className="relative py-32 flex items-center justify-center bg-[#111827]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <div className="flex justify-center mb-6">
            <Shield size={64} className="text-secondary" />
          </div>
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-4">
            Legal & Compliance
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Our commitment to transparency, confidentiality, and legal compliance
          </p>
        </div>
      </section>

      {/* Legal Sections */}
      <section data-testid="legal-content" className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="space-y-8">
            {legalContent.map((item) => (
              <div
                key={item.section}
                data-testid={`legal-${item.section}`}
                className="glass-card p-8 rounded-sm"
              >
                <h2 className="font-heading text-2xl font-semibold text-secondary mb-4">
                  {getSectionTitle(item.section)}
                </h2>
                <p className="text-gray-300 text-base leading-relaxed">
                  {getLangContent(item.content)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LLP Details */}
      {llpDetails && (
        <section data-testid="llp-details" className="py-24 md:py-32 bg-[#111827]">
          <div className="container mx-auto px-4 md:px-8 max-w-4xl">
            <h2 className="font-heading text-3xl font-semibold text-white mb-8 text-center">
              Company Registration Details
            </h2>
            <div className="glass-card p-8 rounded-sm space-y-4">
              <div>
                <span className="text-gray-400 text-sm uppercase tracking-wider">Company Name</span>
                <p className="text-white text-lg font-medium mt-1">{getLangContent(llpDetails.company_name)}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm uppercase tracking-wider">CIN</span>
                <p className="text-white text-lg font-medium mt-1">{llpDetails.cin}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm uppercase tracking-wider">Registered Address</span>
                <p className="text-white text-lg font-medium mt-1">{getLangContent(llpDetails.registered_address)}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                <div>
                  <span className="text-gray-400 text-sm uppercase tracking-wider">Email</span>
                  <p className="text-white text-base font-medium mt-1">{llpDetails.email}</p>
                </div>
                <div>
                  <span className="text-gray-400 text-sm uppercase tracking-wider">Phone</span>
                  <p className="text-white text-base font-medium mt-1">{llpDetails.phone}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
